import { useMemo, useState } from "react"
import { useQueryState } from "nuqs"
import IllustrationResultsTable from "../../components/IllustrationResultsTable"
import useIllustrationResult from "../../hooks/useIllustrationResult"
import usePagination from "../../hooks/usePagination"
import { generateFilter } from "../../utils"
import ApiError from "../../components/ApiError"

const FILTER_COLUMN_IDS = [
  "accessionNumber",
  "viewType",
  "spineRegion",
  "curvatureType",
  "filePath",
  "createdAt",
]

const Illustrations = () => {
  const { limit, onPaginationChange, page, pagination } = usePagination()
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }])

  const [accession, setAccession] = useQueryState("accession", {
    defaultValue: "",
  })
  const [viewType, setViewType] = useQueryState("viewType", {
    defaultValue: "",
  })
  const [region, setRegion] = useQueryState("region", { defaultValue: "" })
  const [curvature, setCurvature] = useQueryState("curvature", {
    defaultValue: "",
  })
  const [filePath, setFilePath] = useQueryState("filePath", {
    defaultValue: "",
  })
  const [createdAt, setCreatedAt] = useQueryState("createdAt", {
    defaultValue: "",
  })

  const urlValueByColumnId = {
    accessionNumber: accession,
    viewType,
    spineRegion: region,
    curvatureType: curvature,
    filePath,
    createdAt,
  }

  const setUrlValueByColumnId = {
    accessionNumber: setAccession,
    viewType: setViewType,
    spineRegion: setRegion,
    curvatureType: setCurvature,
    filePath: setFilePath,
    createdAt: setCreatedAt,
  }

  const filters = useMemo(
    () =>
      FILTER_COLUMN_IDS.map((colId) => ({
        id: colId,
        value: urlValueByColumnId[colId],
      })).filter((f) => f.value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accession, viewType, region, curvature, filePath, createdAt]
  )

  const setFilters = (updater) => {
    const next = typeof updater === "function" ? updater(filters) : updater
    for (const colId of FILTER_COLUMN_IDS) {
      const newVal = next.find((f) => f.id === colId)?.value ?? ""
      setUrlValueByColumnId[colId](newVal || null)
    }
  }

  const { data, isLoading, error, isError } = useIllustrationResult({
    page,
    limit,
    sortBy: sorting.map((s) => `${s.id}:${s.desc ? "DESC" : "ASC"}`).join(","),
    filters: generateFilter(filters),
  })

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div>
      <IllustrationResultsTable
        data={isLoading ? [] : data.data}
        filters={filters}
        loading={isLoading}
        pageCount={isLoading ? 0 : data.pages}
        pagination={pagination}
        setFilters={setFilters}
        setSorting={setSorting}
        sorting={sorting}
        onPaginationChange={onPaginationChange}
      />
    </div>
  )
}

export default Illustrations
